import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router'
import { PAYMENT_TIMING_LABELS, formatAmount } from '@concours/shared'
import {
  Alert,
  Badge,
  Button,
  Can,
  Card,
  CardBody,
  CardHeader,
  InfoGrid,
  InfoItem,
  useAuth,
  usePermissions,
  useToast,
} from '@concours/ui'
import { errorMessage } from '../../lib/errors'
import { when, type CompetitionDetail } from '../../lib/competition-types'

interface Selection {
  selected: string[]
  methods: {
    id: string
    label: string
    provider: string
    isActive: boolean
    environment: 'sandbox' | 'live'
  }[]
}

/**
 * Paiement d'un concours (ADR-001, ADR-002) : rappel des frais et du moment du
 * paiement, moyens acceptés (sous-ensemble des moyens actifs ; aucun choix =
 * tous les moyens actifs).
 */
export function PaymentTab({ c }: { c: CompetitionDetail }) {
  const { session } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const { can } = usePermissions()
  const key = ['admin', 'competitions', c.id, 'payment-methods']
  const data = useQuery({
    queryKey: key,
    queryFn: () => session.api.get<Selection>(`/admin/competitions/${c.id}/payment-methods`),
  })
  const [chosen, setChosen] = useState<string[] | null>(null)
  const selected = chosen ?? data.data?.selected ?? []
  const save = useMutation({
    mutationFn: () =>
      session.api.put<{ selected: string[] }>(`/admin/competitions/${c.id}/payment-methods`, {
        methods: selected,
      }),
    onSuccess: async () => {
      toast.success('Moyens de paiement du concours enregistrés')
      setChosen(null)
      await queryClient.invalidateQueries({ queryKey: key })
    },
    onError: (error) => toast.error(errorMessage(error)),
  })
  const editable = can('concours.update', { type: 'competition', id: c.id })
  const dirty = chosen !== null
  const free = c.paymentTiming === 'not_required' || c.feeAmount <= 0

  return (
    <div className="stack" style={{ gap: 'var(--space-5)' }}>
      <Card>
        <CardHeader title="Frais d’inscription" />
        <CardBody>
          <InfoGrid>
            <InfoItem label="Montant">{formatAmount(c.feeAmount, c.currency)}</InfoItem>
            <InfoItem label="Moment du paiement">{PAYMENT_TIMING_LABELS[c.paymentTiming]}</InfoItem>
            <InfoItem label="Date limite de paiement">{when(c.paymentDeadlineAt)}</InfoItem>
          </InfoGrid>
        </CardBody>
      </Card>
      <Card>
        <CardHeader title="Moyens de paiement acceptés" />
        <CardBody>
          {free ? (
            <Alert tone="info">Aucun paiement n’est demandé pour ce concours.</Alert>
          ) : (
            <div className="stack" style={{ gap: 'var(--space-3)' }}>
              <p className="text-muted" style={{ margin: 0 }}>
                {selected.length
                  ? 'Seuls les moyens cochés (et actifs) sont proposés aux candidats de ce concours.'
                  : 'Aucun moyen coché : tous les moyens actifs sont proposés.'}
              </p>
              {data.isError && <Alert tone="danger">{errorMessage(data.error)}</Alert>}
              <ul className="diff-list">
                {data.data?.methods.map((m) => (
                  <li key={m.id}>
                    <label className="check-row">
                      <input
                        type="checkbox"
                        disabled={!editable}
                        checked={selected.includes(m.id)}
                        onChange={(e) =>
                          setChosen(
                            e.target.checked
                              ? [...selected, m.id]
                              : selected.filter((id) => id !== m.id)
                          )
                        }
                      />
                      <span className="row" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                        <strong>{m.label}</strong>
                        {!m.isActive && <Badge variant="neutral">Inactif</Badge>}
                        {m.environment === 'sandbox' && <Badge variant="warning">Test</Badge>}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
              <Can perm="payment_methods.view">
                <Link
                  to="/moyens-paiement"
                  className="btn btn-ghost btn-sm"
                  style={{ justifySelf: 'start' }}
                >
                  Gérer les moyens de paiement
                </Link>
              </Can>
              {editable && (
                <div className="row" style={{ gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
                  {dirty && (
                    <Button variant="secondary" onClick={() => setChosen(null)}>
                      Annuler
                    </Button>
                  )}
                  <Button
                    variant="primary"
                    disabled={!dirty}
                    loading={save.isPending}
                    onClick={() => save.mutate()}
                  >
                    Enregistrer
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  )
}
